/**
 * PKCE (Proof Key for Code Exchange) utilities — RFC 7636
 *
 * Provides verifier/challenge generation and sessionStorage management.
 * The code verifier is stored ONLY in sessionStorage (tab-scoped).
 * It MUST NOT be stored in localStorage (shared across tabs, race condition risk).
 */

const VERIFIER_KEY = 'pkce_code_verifier';
const VERIFIER_LENGTH = 128;

/**
 * Encode a Uint8Array as a base64url string (no padding, URL-safe alphabet).
 *
 * @param {Uint8Array} bytes
 * @returns {string}
 */
function encodeBase64url(bytes) {
  const base64 = btoa(String.fromCharCode(...bytes));
  return base64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

/**
 * Generate a cryptographically random code verifier.
 * Produces a 128-character base64url string from 96 random bytes,
 * satisfying RFC 7636 §4.1 (43–128 chars, unreserved characters only).
 *
 * @returns {string} 128-character base64url-encoded verifier
 */
export function generateCodeVerifier() {
  // 96 bytes → 128 base64url chars (96 * 4/3 = 128, no padding needed)
  const randomBytes = new Uint8Array(96);
  crypto.getRandomValues(randomBytes);
  return encodeBase64url(randomBytes);
}

/**
 * Generate a PKCE code challenge from a verifier using SHA-256 (method S256).
 *
 * @param {string} verifier - The code verifier produced by generateCodeVerifier()
 * @returns {Promise<string>} base64url-encoded SHA-256 hash of the verifier
 */
export async function generateCodeChallenge(verifier) {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return encodeBase64url(new Uint8Array(hashBuffer));
}

/**
 * Persist the code verifier to sessionStorage for retrieval after the redirect.
 *
 * @param {string} verifier
 */
export function storeVerifier(verifier) {
  sessionStorage.setItem(VERIFIER_KEY, verifier);
}

/**
 * Retrieve the stored code verifier from sessionStorage.
 *
 * @returns {string|null} The verifier, or null if not present
 */
export function retrieveVerifier() {
  return sessionStorage.getItem(VERIFIER_KEY);
}

/**
 * Remove the code verifier from sessionStorage.
 * Must be called after the code exchange to prevent reuse.
 */
export function clearVerifier() {
  sessionStorage.removeItem(VERIFIER_KEY);
}
